import PropTypes from 'prop-types';
// @flow

import React from 'react';
import _ from 'lodash';

import I18n from 'PMT/i18n/I18n';

// -- constants
import ViewConstants from 'constants/ViewConstants';
import ApiConstants from 'PMT/constants/ApiConstants';

// -- actions
import { RestaurantMenuAction, RouteAction } from 'actions';

// -- utils
import SecurityContext from 'utils/permissions/SecurityContext';
import Notify from 'PMT/utils/Notify';

// -- entities
import UpsellingCategory from 'PMT/entities/menu/UpsellingCategory';
import RestaurantMenu from 'PMT/entities/menu/RestaurantMenu';
import CategoryItem from 'PMT/entities/menu/CategoryItem';
import Product from 'PMT/entities/menu/Product';
import Menu from 'PMT/entities/menu/Menu';

// -- views
import AbstractRestaurantMenuForm from '../../components/AbstractRestaurantMenuForm';
import FixedSubmitArea from 'PMT/components/form/FixedSubmitArea';
import UpsellingCategoryDetailProductRow from './components/UpsellingCategoryDetailProductRow';
import UpsellingCategoryDetailMenuRow from './components/UpsellingCategoryDetailMenuRow';
import { RoundButton } from 'components';
import SortableList from 'PMT/components/SortableList';
import AddItemToUpsellingCategoryModal from './addItemToCategory/AddItemToUpsellingCategoryModal';
import EditUpsellingCategoryModal from 'components/restaurantMenu/upsellingCategoryModal/EditUpsellingCategoryModal';
import EnabledDisabledIcon from 'PMT/components/EnabledDisabledIcon';

export default class UpsellingCategoryDetailView extends AbstractRestaurantMenuForm {

  static propTypes = {
    upsellingCategory: PropTypes.objectOf(UpsellingCategory).isRequired,

    restaurantMenu: PropTypes.objectOf(RestaurantMenu).isRequired,

    onUpdate: PropTypes.func.isRequired,

    currencyCode: PropTypes.string,

  };

  componentWillReceiveProps(nextProps: Object) {
    super.componentWillReceiveProps(nextProps);
  }

  getDefaultState(props: Object) {
    return {
      upsellingCategory: props.upsellingCategory.copy(),

      displayAddItemModal: false,

      image: null,

      displayEditModal: false
    };
  }

  shouldUpdateState(nextProps: Object) {
    return !this.props.upsellingCategory.equals(nextProps.upsellingCategory);
  }

  onOrderChange = (items) => {
    const upsellingCategory: UpsellingCategory = this.upsellingCategory;

    upsellingCategory.items = items;

    this.setState({
      upsellingCategory
    });
  };

  onDeleteProductFromCategory = (product: Product) => {
    Notify.confirm(I18n.tr('menu.upselling_category.detail.confirm_delete_product'), (result) => {
      if (result) {
        const upsellingCategory: UpsellingCategory = this.upsellingCategory;

        upsellingCategory.removeItemWithId(product.id);

        this.setState({
          upsellingCategory
        });
      }
    });
  };

  onDeleteMenuFromCategory = (menu: Menu) => {
    Notify.confirm(I18n.tr('menu.upselling_category.detail.confirm_delete_menu'), (result) => {
      if (result) {
        const upsellingCategory: UpsellingCategory = this.upsellingCategory;

        upsellingCategory.removeItemWithId(menu.id);

        this.setState({
          upsellingCategory
        });
      }
    });
  };

  // -- events navigation
  onDisplayProductDetail = (product: Product) => {
    RouteAction.presentProductEdit(product);
  };

  onDisplayMenuDetail = (menu: Menu) => {
    RouteAction.presentMenuDetail(menu);
  };


  //
  // Edit modal events
  //

  onEditClicked = () => {
    this.setState({
      displayEditModal: true
    });
  };

  onHideEditModal = () => {
    this.setState({
      displayEditModal: false
    });
  };


  // -- getters

  get upsellingCategory(): UpsellingCategory {
    return this.state.upsellingCategory;
  }

  get currencyCode(): string {
    return this.props.currencyCode;
  }


  get haveModifications(): boolean {
    return !this.props.upsellingCategory.equals(this.state.upsellingCategory)
      || !_.isNull(this.state.image);
  }

  onFormSubmit = () => {
    this.props.onUpdate(this.upsellingCategory, this.state.image);
  };

  onFormReset = () => {
    this.setState({
      upsellingCategory: this.props.upsellingCategory.copy(),
      image: null
    });
  };

  // -- events add item modal

  onDisplayAddItemModal = () => {
    this.setState({
      displayAddItemModal: true
    });
  };

  onHideAddItemModal = () => {
    this.setState({
      displayAddItemModal: false
    });
  };

  // -- image

  onUpsellingCategoryChange = (upsellingCategory: UpsellingCategory) => {
    this.setState({
      upsellingCategory
    });
  }

  // -- renderers


  renderTheadContentItemList() {
    return (
      <div className="thead col-xs-12">
        <div className="col-md-2 hidden-sm"/>
        <div className="col-xs-6">
          {I18n.tr('menu.upselling_category.detail.table.header.name')}
        </div>
        <div className="col-xs-2 text-left">
          {I18n.tr('menu.upselling_category.detail.table.header.modes_and_price')}
        </div>

        {SecurityContext.restaurantMenu.canEdit() &&
          <div className="col-xs-1 text-center">
            {I18n.tr('menu.upselling_category.detail.table.header.delete_category')}
          </div>
        }

        {SecurityContext.restaurantMenu.canEdit() &&
          <div className="col-xs-1 text-center">
            {I18n.tr('menu.upselling_category.detail.table.header.move')}
          </div>
        }

      </div>
    );
  }

  renderRow = (item: CategoryItem) => {
    if (item.isProduct()) {
      const product: Product = this.props.restaurantMenu.getProductWithId(item.id);

      return (
        <UpsellingCategoryDetailProductRow
          product={product}
          key={product.id}
          onDeleteFromCategory={this.onDeleteProductFromCategory}
          currencyCode={this.currencyCode}
        />
      );
    } else if (item.isMenu()) {
      const menu: Menu = this.props.restaurantMenu.getMenuWithId(item.id);

      return (
        <UpsellingCategoryDetailMenuRow
          menu={menu}
          key={menu.id}
          onDeleteFromCategory={this.onDeleteMenuFromCategory}
          currencyCode={this.currencyCode}
        />
      );
    }

    return (null);
  };

  renderItemsList() {
    return (
      <SortableList
        items={this.upsellingCategory.items}
        onChange={this.onOrderChange}
        renderer={this.renderRow}
        displayDragIcon={false}
      />
    );
  }

  renderDescription() {
    if (_.isEmpty(this.upsellingCategory.description)) {
      return (
        <p><em>{I18n.tr('menu.upselling_category.detail.no_description')}</em></p>
      );
    }

    return (
      <p>{this.upsellingCategory.description}</p>
    );
  }

  renderSubmitArea() {
    return (
      <FixedSubmitArea
        onSubmit={this.onFormSubmit}
        onReset={this.onFormReset}
        haveModifications={this.haveModifications}
      />
    );
  }

  renderAddItemToUpsellingCategoryModal() {
    return (
      <AddItemToUpsellingCategoryModal
        show={this.state.displayAddItemModal}
        onHide={this.onHideAddItemModal}
        restaurantMenu={this.props.restaurantMenu}
        upsellingCategory={this.state.upsellingCategory}
      />
    );
  }

  renderEditModal() {
    if (this.state.displayEditModal) {
      return (
        <EditUpsellingCategoryModal
          upsellingCategory={this.upsellingCategory}
          onUpdate={this.onUpsellingCategoryChange}
          show={this.state.displayEditModal}
          onHide={this.onHideEditModal}
        />
      );
    }

    return (null);
  }

  renderEditButton() {
    if (!SecurityContext.restaurantMenu.canEdit()) {
      return (null);
    }

    return (
      <button
        className="btn btn-primary"
        onClick={this.onEditClicked}
      >
        <i className="fa fa-edit" />
        &nbsp;&nbsp;
        {I18n.tr('menu.upselling_category.detail.edit')}
      </button>
    );
  }

  render() {
    return (
      <div className="product_category_view">

        <div className="row">
          <div className="col-xs-12 col-sm-10">
            <h3>{this.upsellingCategory.name}</h3>
            {this.renderDescription()}
          </div>

          <div className="col-sm-2">
            {this.renderEditButton()}
          </div>
        </div>

        <hr />

        {SecurityContext.restaurantMenu.canEdit() &&
          <div className="top-buffer-30">
            <RoundButton
              text={I18n.tr('menu.upselling_category.detail.add_product_or_menu')}
              onClick={this.onDisplayAddItemModal}
            />
          </div>
        }

        <div className="top-buffer-30 table_container table_container_hover row">
          {this.renderTheadContentItemList()}
          {this.renderItemsList()}
        </div>

        {this.renderSubmitArea()}

        {this.renderAddItemToUpsellingCategoryModal()}
        {this.renderLoading()}
        {this.renderEditModal()}
      </div>
    );
  }

}



// WEBPACK FOOTER //
// ./scripts/pages/menu/upsellingCategories/detail/UpsellingCategoryDetailView.js